/**
 * @fileoverview HTTP and WebSocket client for Sandbox Platform SDK
 */

import fetch from 'cross-fetch'
import { EventEmitter } from 'eventemitter3'
import {
  ApiResponse,
  SandboxClientConfig,
  WebSocketMessage,
  WebSocketEventType
} from './types'
import { AuthManager } from './auth'
import {
  NetworkError,
  AuthenticationError,
  RateLimitError,
  createErrorFromResponse
} from './errors'

/**
 * HTTP client for API requests with retry logic
 */
export class HttpClient {
  private authManager: AuthManager
  private timeout: number
  private retryAttempts: number
  private retryDelay: number
  private debug: boolean
  private pendingRequests = new Map<string, Promise<any>>()

  constructor(config: SandboxClientConfig = {}) {
    this.authManager = new AuthManager(config.auth)
    this.timeout = config.timeout || 30000
    this.retryAttempts = config.retryAttempts || 3
    this.retryDelay = config.retryDelay || 1000
    this.debug = config.debug || false

    // Validate authentication on initialization
    this.authManager.validateAuth()
  }

  /**
   * Make an HTTP request with retry logic
   */
  async request<T = any>(
    method: string,
    endpoint: string,
    body?: any,
    params?: Record<string, string>,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint, params)
    const requestHeaders = this.authManager.createHeaders(headers)

    if (this.debug) {
      console.log(`[SDK] ${method} ${url}`)
      if (body) {
        console.log('[SDK] Request body:', body)
      }
    }

    // Request deduplication - prevent identical concurrent requests
    const requestKey = `${method}:${url}:${JSON.stringify(body || {})}`
    if (this.pendingRequests.has(requestKey)) {
      if (this.debug) {
        console.log(`[SDK] Deduplicating identical request: ${requestKey}`)
      }
      return this.pendingRequests.get(requestKey)!
    }

    // Create the request execution promise
    const requestPromise = this.executeRequest<T>(method, url, requestHeaders, body)

    // Store the promise for deduplication
    this.pendingRequests.set(requestKey, requestPromise)

    try {
      return await requestPromise
    } finally {
      // Always clean up the pending request
      this.pendingRequests.delete(requestKey)
    }
  }

  /**
   * Execute the actual HTTP request with retry logic
   */
  private async executeRequest<T>(
    method: string,
    url: string,
    headers: Record<string, string>,
    body?: any
  ): Promise<ApiResponse<T>> {
    let lastError: Error

    for (let attempt = 0; attempt <= this.retryAttempts; attempt++) {
      try {
        const response = await this.makeRequest(url, {
          method,
          headers,
          body: body ? JSON.stringify(body) : undefined
        })

        const data = await this.parseResponse<T>(response)

        if (this.debug) {
          console.log('[SDK] Response:', data)
        }

        return data

      } catch (error) {
        lastError = error as Error

        // Don't retry authentication errors, rate limit errors, or client errors
        if (error instanceof AuthenticationError ||
            error instanceof RateLimitError ||
            (error instanceof NetworkError && error.statusCode && error.statusCode < 500)) {
          throw error
        }

        // Don't retry on the last attempt
        if (attempt === this.retryAttempts) {
          throw error
        }

        // Wait before retrying with exponential backoff
        const delay = this.retryDelay * Math.pow(2, attempt)
        await this.sleep(delay)

        if (this.debug) {
          console.log(`[SDK] Retrying request (attempt ${attempt + 2}/${this.retryAttempts + 1}) after ${delay}ms`)
        }
      }
    }

    throw lastError!
  }

  /**
   * Make the actual HTTP request
   */
  private async makeRequest(url: string, options: any): Promise<Response> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      })

      clearTimeout(timeoutId)
      return response

    } catch (error: any) {
      clearTimeout(timeoutId)

      if (error.name === 'AbortError') {
        throw NetworkError.timeout()
      }

      if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
        throw NetworkError.connectionFailed()
      }

      throw new NetworkError(error.message || 'Network request failed')
    }
  }

  /**
   * Parse HTTP response
   */
  private async parseResponse<T>(response: Response): Promise<ApiResponse<T>> {
    let responseData: any = {}

    try {
      const text = await response.text()
      if (text) {
        responseData = JSON.parse(text)
      }
    } catch (error) {
      // Response might not be JSON, that's okay
    }

    // Handle HTTP error status codes
    if (!response.ok) {
      if (response.status === 401) {
        throw new AuthenticationError(
          responseData.error || 'Authentication failed',
          responseData.code
        )
      }

      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After')
        throw new RateLimitError(
          responseData.error || 'Rate limit exceeded',
          retryAfter ? parseInt(retryAfter, 10) : undefined
        )
      }

      throw createErrorFromResponse(responseData, response.status)
    }

    return responseData
  }

  /**
   * Build full URL with query parameters
   */
  private buildUrl(endpoint: string, params?: Record<string, string>): string {
    const baseUrl = this.authManager.getBaseUrl()
    const url = new URL(endpoint.startsWith('/') ? endpoint.slice(1) : endpoint, `${baseUrl}/`)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value)
      })
    }

    return url.toString()
  }

  /**
   * Sleep for a specified number of milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * GET request
   */
  async get<T = any>(
    endpoint: string,
    params?: Record<string, string>,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('GET', endpoint, undefined, params, headers)
  }

  /**
   * POST request
   */
  async post<T = any>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, body, undefined, headers)
  }

  /**
   * PUT request
   */
  async put<T = any>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, body, undefined, headers)
  }

  /**
   * DELETE request
   */
  async delete<T = any>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint, undefined, undefined, headers)
  }

  /**
   * Get the authentication manager
   */
  getAuthManager(): AuthManager {
    return this.authManager
  }
}

/**
 * WebSocket client for real-time communication
 */
export class WebSocketClient extends EventEmitter {
  private ws: WebSocket | null = null
  private authManager: AuthManager
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000
  private debug: boolean

  constructor(authManager: AuthManager, debug = false) {
    super()
    this.authManager = authManager
    this.debug = debug
  }

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const baseUrl = this.authManager.getBaseUrl()
        const wsUrl = baseUrl.replace(/^http/, 'ws') + '/ws'

        if (this.debug) {
          console.log('[SDK] Connecting to WebSocket:', wsUrl)
        }

        this.ws = new WebSocket(wsUrl, {
          headers: {
            'Authorization': this.authManager.getAuthorizationHeader()
          }
        } as any)

        this.ws.onopen = () => {
          if (this.debug) {
            console.log('[SDK] WebSocket connected')
          }
          this.reconnectAttempts = 0
          this.emit('connected')
          resolve()
        }

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data)
            if (this.debug) {
              console.log('[SDK] WebSocket message:', message)
            }
            this.handleMessage(message)
          } catch (error) {
            console.error('[SDK] Failed to parse WebSocket message:', error)
          }
        }

        this.ws.onclose = (event) => {
          if (this.debug) {
            console.log('[SDK] WebSocket closed:', event.code, event.reason)
          }
          this.emit('disconnected', event.code, event.reason)
          this.handleReconnect()
        }

        this.ws.onerror = (error) => {
          console.error('[SDK] WebSocket error:', error)
          this.emit('error', error)
          reject(error)
        }

      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Send message through WebSocket
   */
  send(message: Partial<WebSocketMessage>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected')
    }

    const fullMessage: WebSocketMessage = {
      event: message.event!,
      data: message.data,
      sandboxId: message.sandboxId,
      timestamp: new Date().toISOString()
    }

    this.ws.send(JSON.stringify(fullMessage))

    if (this.debug) {
      console.log('[SDK] WebSocket sent:', fullMessage)
    }
  }

  /**
   * Close WebSocket connection
   */
  disconnect(): void {
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.removeAllListeners()
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(message: WebSocketMessage): void {
    this.emit('message', message)
    this.emit(message.event, message.data, message.sandboxId)
  }

  /**
   * Handle WebSocket reconnection
   */
  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SDK] Max reconnection attempts reached')
      return
    }

    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1)

    if (this.debug) {
      console.log(`[SDK] Reconnecting WebSocket in ${delay}ms (attempt ${this.reconnectAttempts})`)
    }

    setTimeout(() => {
      this.connect().catch((error) => {
        console.error('[SDK] WebSocket reconnection failed:', error)
      })
    }, delay)
  }
}